import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../assets/css/gamesCss/Tree/HeapIntro.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const HeapIntro = () => {
    const [treeNodes, setTreeNodes] = useState(Array(14).fill(null));
    const [availableNodes, setAvailableNodes] = useState([]);
    const [draggedNode, setDraggedNode] = useState(null);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLifesLeft] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();


    const mission = selLanguage ? "Δημιούργησε ένα Max Heap τοποθετώντας τους κόμβους στις σωστές θέσεις.Σύρε τους κόμβους από κάτω στο δέντρο μέχρι να ολοκληρωθεί το δέντρο." : "Create a Max Heap by placing the nodes in the correct positions.Drag the nodes from below into the tree until the tree is complete."

    useEffect(() => {
        initializeGame();
    }, []);

    useEffect(() => {
        if (lives <= 0 && gameState !== "won") {
            setShowGameOver(true);
            setGameState("gameOver");
        }
    }, [lives, gameState]);

    const initializeGame = () => {
        const numbers = Array.from({ length: 14 }, (_, i) => String(i + 1));
        const shuffled = [...numbers].sort(() => 0.5 - Math.random());
        setTreeNodes(Array(14).fill(null));
        setAvailableNodes(shuffled);
        setMessage("");
        setGameState("playing");
        setLifesLeft(3);
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const handleDragStart = (e, node) => {
        setDraggedNode(node);
        e.dataTransfer.setData("text/plain", node);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDropOnNode = (index) => (e) => {
        e.preventDefault();
        const node = e.dataTransfer.getData("text/plain");
        if (!node || treeNodes[index] || gameState !== "playing") return;
        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[index] = node;
            return updated;
        });
        setAvailableNodes((prev) => prev.filter((n) => n !== node));
    };

    const handleRemoveNode = (index) => {
        if (gameState !== "playing") return;
        const node = treeNodes[index];
        if (!node) return;
        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[index] = null;
            return updated;
        });
        setAvailableNodes((prev) => [...prev, node]);
    };

    const checkSolution = () => {
        if (gameState !== "playing") return;

        // Έλεγχος αν όλοι οι κόμβοι έχουν τοποθετηθεί
        const allNodesPlaced = treeNodes.every(node => node !== null);
        if (!allNodesPlaced) {
            setMessage(selLanguage?" Πρέπει να τοποθετήσεις όλους τους κόμβους πρώτα!":" You need to place all nodes first!");
            setLifesLeft(lives - 1);
            return;
        }

        // Έλεγχος Max Heap property: κάθε γονέας >= παιδιά του
        let isValidMaxHeap = true;

        for (let i = 0; i < treeNodes.length; i++) {
            const parentValue = parseInt(treeNodes[i]);
            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;

            // Έλεγχος αριστερού παιδιού
            if (leftChildIndex < treeNodes.length) {
                const leftChildValue = parseInt(treeNodes[leftChildIndex]);
                if (parentValue < leftChildValue) {
                    isValidMaxHeap = false;
                    break;
                }
            }

            // Έλεγχος δεξιού παιδιού
            if (rightChildIndex < treeNodes.length) {
                const rightChildValue = parseInt(treeNodes[rightChildIndex]);
                if (parentValue < rightChildValue) {
                    isValidMaxHeap = false;
                    break;
                }
            }
        }

        if (isValidMaxHeap) {
            setGameState("won");
            setShowGameWon(true);
        } else {
            const newLives = lives - 1;
            setLifesLeft(newLives);

            if (newLives <= 0) {
                setShowGameOver(true);
            } else {
                setMessage(selLanguage?" Το δέντρο δεν είναι Max Heap! Κάποιος γονέας είναι μικρότερος από το παιδί του. Προσπάθησε ξανά!":" The tree is not a Max Heap! A parent is smaller than its child. Try again!");
            }
        }
    };

    const handlePlayAgain = () => {
        initializeGame();
    };



    const getNodePositions = (level, nodeIndex) => {

        const baseSpacing = 600;
        let levelSpacing = baseSpacing / Math.pow(2, level);
        levelSpacing = Math.max(levelSpacing, 70);
        const nodesInLevel = Math.pow(2, level);
        const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
        const startOffset = -(nodesInLevel - 1) * levelSpacing / 2;

        return {
            x: startOffset + indexInLevel * levelSpacing,
            y: level * 100
        };
    };


    const getNodeLevel = (index) => {
        return Math.floor(Math.log2(index + 1));
    };

    const renderEdges = () => {
        const edges = [];

        for (let i = 0; i < treeNodes.length; i++) {
            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;

            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i);

            if (leftChildIndex < treeNodes.length) {
                const childLevel = getNodeLevel(leftChildIndex);
                const childPos = getNodePositions(childLevel, leftChildIndex);

                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="3"
                        opacity={gameState === "playing" ? 1 : 0.6}
                    />
                );
            }

            if (rightChildIndex < treeNodes.length) {
                const childLevel = getNodeLevel(rightChildIndex);
                const childPos = getNodePositions(childLevel, rightChildIndex);

                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="3"
                        opacity={gameState === "playing" ? 1 : 0.6}
                    />
                );
            }
        }

        return edges;
    };

    const renderNode = (index) => {
        const level = getNodeLevel(index);
        const position = getNodePositions(level, index);
        const nodeValue = treeNodes[index];

        return (
            <g key={index}>
                <motion.circle
                    whileHover={gameState === "playing" ? { scale: 1.1 } : {}}
                    whileTap={gameState === "playing" ? { scale: 0.95 } : {}}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    onClick={() => handleRemoveNode(index)}
                    cx={position.x}
                    cy={position.y + 25}
                    r="25"
                    fill={nodeValue ? "#3b82f6" : "#e5e7eb"}
                    stroke={nodeValue ? "rgba(255,255,255,0.2)" : "#9ca3af"}
                    strokeWidth="2"
                    strokeDasharray={nodeValue ? "0" : "5,5"}
                    style={{
                        cursor: gameState === "playing" ? "pointer" : "not-allowed",
                        filter: nodeValue
                            ? "drop-shadow(0 6px 15px rgba(59, 130, 246, 0.4))"
                            : "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                        transition: "all 0.3s ease"
                    }}
                />

                <text
                    x={position.x}
                    y={position.y + 32}
                    textAnchor="middle"
                    fill={nodeValue ? "white" : "#6b7280"}
                    fontSize="14"
                    fontWeight="bold"
                    style={{ pointerEvents: "none" }}
                >
                    {nodeValue || ""}
                </text>

                <rect
                    x={position.x - 25}
                    y={position.y}
                    width="50"
                    height="50"
                    fill="transparent"
                    onDragOver={handleDragOver}
                    onDrop={handleDropOnNode(index)}
                    style={{ cursor: gameState === "playing" ? "default" : "not-allowed" }}
                />
            </g>
        );
    };

    const renderTree = () => {
        const maxLevel = Math.floor(Math.log2(treeNodes.length));
        const svgWidth = 900;
        const svgHeight = (maxLevel + 2) * 70;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                overflowX: "auto",
                marginTop: "30px",
                marginBottom: "30px",

            }}>
                <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox={`${-svgWidth / 2} 0 ${svgWidth} ${svgHeight}`}
                    style={{ minWidth: "700px" }}
                >
                    {renderEdges()}
                    {treeNodes.map((_, index) => renderNode(index))}
                </svg>
            </div>
        );
    };

    return (
        <div
            className='heapIntroContainer main-content'
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}

            />
            <div style={{ width: "100%", maxWidth: "1200px" }}>
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: "5px" }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        justifyContent: "right",
                        gap: "12px"
                    }}>


                        <div style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center"
                        }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease",
                                        animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>

                    <style>{`
                            @keyframes heartbeat {
                                0%, 100% { transform: scale(1); }
                                50% { transform: scale(1.1); }
                            }
                        `}</style>
                </div>

                <br />

                <div className="heapIntroPron">
                    <p style={{ margin: 0, lineHeight: "1.6" }}>
                        {mission}
                    </p>
                </div>

                {renderTree()}

                <div className="heapIntroRemainingNodes">
                    <h3 style={{ fontWeight: "600", marginBottom: "20px", color: "white" }}>
                        {selLanguage?"Διαθέσιμοι Κόμβοι:":"Available Nodes:"}
                    </h3>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                        {availableNodes.map((node) => (
                            <motion.div
                                className="heapIntroNodes"
                                key={node}
                                draggable={gameState === "playing"}
                                onDragStart={(e) => gameState === "playing" && handleDragStart(e, node)}
                                whileHover={gameState === "playing" ? { scale: 1.1 } : {}}
                                whileTap={gameState === "playing" ? { scale: 0.95 } : {}}
                                style={{
                                    opacity: gameState === "playing" ? 1 : 0.6,
                                    cursor: gameState === "playing" ? "grab" : "not-allowed"
                                }}
                            >
                                {node}
                            </motion.div>
                        ))}
                    </div>
                </div>

                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                                padding: "16px",
                                borderRadius: "8px",
                                backgroundColor: gameState === "won" ? "#dcfce7" : gameState === "gameOver" ? "#450a0a" : "#fee2e2",
                                color: gameState === "won" ? "#166534" : gameState === "gameOver" ? "#fca5a5" : "#991b1b",
                                textAlign: "center",
                                marginBottom: "16px",
                                border: `1px solid ${gameState === "won" ? '#bbf7d0' : gameState === "gameOver" ? '#dc2626' : '#fecaca'}`
                            }}
                        >
                            <strong>{message}</strong>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    width: "100%",
                    maxWidth: "400px",
                    margin: "0 auto"
                }}>
                    <motion.button
                        className="heapIntroCheck"
                        whileHover={gameState === "playing" ? { scale: 1.05 } : {}}
                        whileTap={gameState === "playing" ? { scale: 0.95 } : {}}
                        onClick={checkSolution}
                        disabled={gameState !== "playing"}
                        style={{
                            opacity: gameState === "playing" ? 1 : 0.6,
                            cursor: gameState === "playing" ? "pointer" : "not-allowed"
                        }}
                    >
                        {selLanguage?"Έλεγχος":"Check"}
                    </motion.button>

                    <motion.button
                        className="heapIntroDelete"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={initializeGame}
                        onMouseEnter={(e) => {
                            e.target.style.boxShadow = "0 15px 35px rgba(184, 30, 30, 0.4), 0 0 0 2px rgba(184, 30, 30, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.boxShadow = "0 10px 25px rgba(184, 30, 30, 0.3), 0 0 0 1px rgba(184, 30, 30, 0.2)";
                        }}
                    >
                        {selLanguage?"Καθαρισμός":"Reset"}
                    </motion.button>
                </div>
            </div>
        </div>
    );
};

export default HeapIntro;